import { useMutation, useQuery } from '@/hooks'
import { useQueryClient } from '@tanstack/react-query'

export type TempWorker = {
  id: string
  name: string
  phone?: string | null
  position?: string | null
  active: boolean
  paid_through_work_date?: string | null
  credit_balance?: number
  created_at?: string
  updated_at?: string
}

export type TempWorkSession = {
  id: string
  work_date: string
  location?: string | null
  temp_worker_id?: string | null
  worker_name_snapshot: string
  worker_position_snapshot?: string | null
  check_in_at: string
  check_out_at?: string | null
  pay_baht?: number | null
  note?: string | null
  created_at?: string
  updated_at?: string
}

export type TempWorkSessionsResponse = {
  data: {
    sessions: TempWorkSession[]
    total_pay_baht: number
    /** @deprecated legacy open list */
    open?: TempWorkSession[]
    /** @deprecated use sessions */
    closed?: TempWorkSession[]
  }
}

export type TempWorkerWorkSummary = {
  total_pay_baht: number
  closed_sessions: number
  open_sessions: number
  working_days: number
  total_hours: number
}

export type TempWorkerWorkSummaryResponse = {
  data: {
    worker: TempWorker
    summary: TempWorkerWorkSummary
    sessions: TempWorkSession[]
  }
  meta: { total: number; currentPage: number; totalPages: number }
}

export type SettlementPreview = {
  gross_wage_baht: number
  credit_applied_baht: number
  net_cash_paid_baht: number
  remaining_credit_baht: number
  working_days: number
  unpaid_session_count: number
}

export type TempWorkerPaymentTxn = {
  id: string
  temp_worker_id: string
  txn_type: 'advance' | 'settlement'
  amount_baht: number
  credit_applied_baht?: number | null
  gross_wage_baht?: number | null
  cutoff_work_date?: string | null
  start_work_date?: string | null
  note?: string | null
  created_at: string
}

export type TempWorkerPaymentSlipSession = TempWorkSession & {
  hours: number | null
}

export type TempWorkerPaymentSlipSettlement = {
  kind: 'settlement'
  worker: TempWorker
  txn: TempWorkerPaymentTxn
  period: { start: string; end: string }
  paid_through_before: string | null
  sessions: TempWorkerPaymentSlipSession[]
  summary: {
    gross_wage_baht: number
    credit_applied_baht: number
    net_cash_paid_baht: number
    working_days: number
    remaining_credit_baht: number | null
  }
}

export type TempWorkerPaymentSlipAdvance = {
  kind: 'advance'
  worker: TempWorker
  txn: TempWorkerPaymentTxn
}

export type TempWorkerPaymentSlipResponse = {
  data: TempWorkerPaymentSlipSettlement | TempWorkerPaymentSlipAdvance
}

export function tempWorkerSlipPath(workerId: string, txnId: string) {
  return `/admin/temp-workers/${workerId}/slip/${txnId}`
}

export type TempWorkerPaymentSummaryResponse = {
  data: {
    worker: TempWorker
    unpaid_gross_baht: number
    preview: SettlementPreview | null
    transactions: TempWorkerPaymentTxn[]
  }
}

export const useTempWorker = (id: string) => {
  return useQuery<{ data: TempWorker }>({
    queryKey: ['temp_workers', id],
    url: `/temp-workers/${id}`,
    queryConfig: { enabled: Boolean(id) },
  })
}

export const useTempWorkerPaymentSlip = (workerId: string, txnId: string) => {
  return useQuery<TempWorkerPaymentSlipResponse>({
    queryKey: ['temp_workers', workerId, 'payment-slip', txnId],
    url: `/temp-workers/${workerId}/payment-txns/${txnId}/slip`,
    queryConfig: { enabled: Boolean(workerId && txnId) },
  })
}

export const useTempWorkerPaymentSummary = (
  id: string,
  params?: { start_work_date?: string; cutoff_work_date?: string },
) => {
  return useQuery<TempWorkerPaymentSummaryResponse>({
    queryKey: ['temp_workers', id, 'payment-summary', params],
    url: `/temp-workers/${id}/payment-summary`,
    axiosConfig: { params },
    queryConfig: {
      enabled: Boolean(id && params?.cutoff_work_date),
    },
  })
}

export const useTempWorkerWorkSummary = (
  id: string,
  params: {
    start_date: string
    end_date: string
    location?: string
    page?: number
    limit?: number
  },
) => {
  return useQuery<TempWorkerWorkSummaryResponse>({
    queryKey: ['temp_workers', id, 'work-summary', params],
    url: `/temp-workers/${id}/work-summary`,
    axiosConfig: { params },
    queryConfig: {
      enabled: Boolean(id && params.start_date && params.end_date),
    },
  })
}

export type TempWorkerListResponse = {
  data: TempWorker[]
  meta: { total: number; currentPage: number; totalPages: number }
}

export const useTempWorkerList = (params?: {
  include_inactive?: boolean
  search?: string
  page?: number
  limit?: number
}) => {
  return useQuery<TempWorkerListResponse>({
    queryKey: ['temp_workers', params],
    url: '/temp-workers',
    axiosConfig: { params },
  })
}

export const useCreateTempWorker = () => {
  const qc = useQueryClient()
  return useMutation<{ data: TempWorker }, Error, { name: string; phone?: string; position?: string }>(
    ['temp_workers_create'],
    { url: '/temp-workers', method: 'POST' },
    {
      onSuccess: () => qc.invalidateQueries({ queryKey: ['temp_workers'] }),
    },
  )
}

export const useUpdateTempWorker = () => {
  const qc = useQueryClient()
  return useMutation<
    { data: TempWorker },
    Error,
    { id: string; name?: string; phone?: string; position?: string; active?: boolean }
  >(
    ['temp_workers_patch'],
    { url: '/temp-workers/:id', method: 'PATCH' },
    {
      onSuccess: (_data, variables) => {
        qc.invalidateQueries({ queryKey: ['temp_workers'] })
        qc.invalidateQueries({ queryKey: ['temp_workers', variables.id] })
        qc.invalidateQueries({ queryKey: ['temp_workers', variables.id, 'work-summary'] })
        qc.invalidateQueries({ queryKey: ['temp_workers', variables.id, 'payment-summary'] })
      },
    },
  )
}

export const useTempWorkSessions = (params: { work_date: string; location?: string }) => {
  return useQuery<TempWorkSessionsResponse>({
    queryKey: ['temp_work_sessions', params],
    url: '/temp-work-sessions',
    axiosConfig: { params },
    queryConfig: { enabled: Boolean(params.work_date) },
  })
}

export const useRecordTempWorkSession = () => {
  const qc = useQueryClient()
  return useMutation<
    { data: TempWorkSession },
    Error,
    {
      work_date: string
      pay_baht: number
      temp_worker_id?: string
      worker_name?: string
      location?: string
      note?: string
    }
  >(
    ['temp_work_sessions_record_work'],
    { url: '/temp-work-sessions/record-work', method: 'POST' },
    {
      onSuccess: () => qc.invalidateQueries({ queryKey: ['temp_work_sessions'] }),
    },
  )
}

export type BulkUpdateTempWorkSessionsResponse = {
  data: {
    updated: TempWorkSession[]
    failed: { session_id: string; worker_name: string; message: string }[]
  }
}

export const useBulkUpdateTempWorkSessions = () => {
  const qc = useQueryClient()
  return useMutation<
    BulkUpdateTempWorkSessionsResponse,
    Error,
    { session_ids: string[]; work_date: string }
  >(
    ['temp_work_sessions_bulk_patch'],
    { url: '/temp-work-sessions/bulk', method: 'PATCH' },
    {
      onSuccess: (data) => {
        qc.invalidateQueries({ queryKey: ['temp_work_sessions'] })
        const workerIds = new Set<string>()
        for (const session of data.data.updated) {
          const workerId = session.temp_worker_id ?? null
          if (workerId) workerIds.add(workerId)
        }
        for (const workerId of workerIds) {
          qc.invalidateQueries({ queryKey: ['temp_workers', workerId] })
          qc.invalidateQueries({
            queryKey: ['temp_workers', workerId, 'work-summary'],
          })
          qc.invalidateQueries({
            queryKey: ['temp_workers', workerId, 'payment-summary'],
          })
        }
      },
    },
  )
}

export const useUpdateTempWorkSession = () => {
  const qc = useQueryClient()
  return useMutation<
    { data: TempWorkSession },
    Error,
    {
      id: string
      work_date?: string
      pay_baht?: number
      location?: string
      note?: string | null
    }
  >(
    ['temp_work_sessions_patch'],
    { url: '/temp-work-sessions/:id', method: 'PATCH' },
    {
      onSuccess: (data) => {
        qc.invalidateQueries({ queryKey: ['temp_work_sessions'] })
        // When a session belongs to a worker, also refresh worker summaries.
        const workerId = data.data.temp_worker_id ?? null
        if (workerId) {
          qc.invalidateQueries({ queryKey: ['temp_workers', workerId] })
          qc.invalidateQueries({
            queryKey: ['temp_workers', workerId, 'work-summary'],
          })
          qc.invalidateQueries({
            queryKey: ['temp_workers', workerId, 'payment-summary'],
          })
        }
      },
    },
  )
}

export const useDeleteTempWorkSession = () => {
  const qc = useQueryClient()
  return useMutation<
    { success: true; data?: TempWorkSession },
    Error,
    {
      id: string
    }
  >(
    ['temp_work_sessions_delete'],
    { url: '/temp-work-sessions/:id', method: 'DELETE' },
    {
      onSuccess: (data) => {
        qc.invalidateQueries({ queryKey: ['temp_work_sessions'] })
        const workerId = data.data?.temp_worker_id ?? null
        if (workerId) {
          qc.invalidateQueries({ queryKey: ['temp_workers', workerId] })
          qc.invalidateQueries({
            queryKey: ['temp_workers', workerId, 'work-summary'],
          })
          qc.invalidateQueries({
            queryKey: ['temp_workers', workerId, 'payment-summary'],
          })
        }
      },
    },
  )
}

export const useCreateTempWorkerAdvance = () => {
  const qc = useQueryClient()
  return useMutation<
    { data: { worker: TempWorker; transaction: TempWorkerPaymentTxn } },
    Error,
    { id: string; amount_baht: number; note?: string }
  >(
    ['temp_workers_advance'],
    { url: '/temp-workers/:id/advance', method: 'POST' },
    {
      onSuccess: (_data, variables) => {
        qc.invalidateQueries({ queryKey: ['temp_workers', variables.id] })
        qc.invalidateQueries({ queryKey: ['temp_workers', variables.id, 'payment-summary'] })
      },
    },
  )
}

export const useSettleTempWorker = () => {
  const qc = useQueryClient()
  return useMutation<
    {
      data: {
        worker: TempWorker
        settlement: SettlementPreview
        transaction: TempWorkerPaymentTxn
      }
    },
    Error,
    { id: string; cutoff_work_date: string; start_work_date?: string }
  >(
    ['temp_workers_settle'],
    { url: '/temp-workers/:id/settle', method: 'POST' },
    {
      onSuccess: (_data, variables) => {
        qc.invalidateQueries({ queryKey: ['temp_workers', variables.id] })
        qc.invalidateQueries({ queryKey: ['temp_workers', variables.id, 'payment-summary'] })
        qc.invalidateQueries({ queryKey: ['temp_workers', variables.id, 'work-summary'] })
        qc.invalidateQueries({ queryKey: ['temp_work_sessions'] })
      },
    },
  )
}

/** @deprecated use useRecordTempWorkSession */
export const useCheckInTempWorkSession = () => {
  const qc = useQueryClient()
  return useMutation<
    { data: TempWorkSession },
    Error,
    {
      work_date: string
      temp_worker_id?: string
      worker_name?: string
      location?: string
    }
  >(
    ['temp_work_sessions_check_in'],
    { url: '/temp-work-sessions/check-in', method: 'POST' },
    {
      onSuccess: () => qc.invalidateQueries({ queryKey: ['temp_work_sessions'] }),
    },
  )
}

/** @deprecated use useRecordTempWorkSession */
export const useCheckOutTempWorkSession = () => {
  const qc = useQueryClient()
  return useMutation<
    { data: TempWorkSession },
    Error,
    { id: string; pay_baht: number; check_out_at?: string }
  >(
    ['temp_work_sessions_check_out'],
    { url: '/temp-work-sessions/:id/check-out', method: 'POST' },
    {
      onSuccess: () => qc.invalidateQueries({ queryKey: ['temp_work_sessions'] }),
    },
  )
}
